/**
 * Teaser tab of the subscribe popup. Companion of snippets/subscribe-teaser.liquid (the
 * <subscribe-teaser> element rendered by sections/subscribe-modal.liquid).
 *
 * The tab knows nothing about the popup's internals. It opens the popup through the
 * <subscribe-modal> element's open() method (or, failing that, a `subscribe-modal:open` event on
 * document) and listens for `subscribe-modal:opened`, `subscribe-modal:closed` and
 * `subscribe-modal:subscribed` on document.
 *
 * Visibility, in order:
 *   - localStorage `subscribe-modal:subscribed` set: never shown again;
 *   - localStorage `subscribe-teaser:closed` (timestamp) younger than data-close-days: hidden;
 *   - data-display: `always` shows at once, `after_delay` after data-delay seconds,
 *     `after_dismiss` only once the popup has been dismissed (stored or seen live);
 *   - hidden while the popup is open; shown again when it closes, with focus returned to the tab.
 * In the theme editor (data-design-mode) the tab shows at once and stores nothing.
 */

const STORAGE_SUBSCRIBED = 'subscribe-modal:subscribed';
const STORAGE_DISMISSED = 'subscribe-modal:dismissed';
const STORAGE_CLOSED = 'subscribe-teaser:closed';
const DAY = 86400000;

class SubscribeTeaser extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-teaser-open]');
    this.closeButton = this.querySelector('[data-teaser-close]');
    if (!(this.button instanceof HTMLElement)) return;

    this.designMode = this.dataset.designMode === 'true';
    this.sectionId = this.dataset.sectionId || '';
    this.display = this.dataset.display || 'always';
    this.allowed = false;
    this.closedByVisitor = false;
    this.subscribed = false;
    this.modalOpen = document.querySelector('subscribe-modal dialog[open]') !== null;
    this.timer = 0;

    this.onOpenClick = () => this.openModal();
    this.onCloseClick = () => this.closeTab();
    this.onModalOpened = (event) => {
      if (!this.isMine(event)) return;
      this.modalOpen = true;
      this.render();
    };
    this.onModalClosed = (event) => {
      if (!this.isMine(event)) return;
      this.modalOpen = false;
      if (event.detail && event.detail.subscribed) this.subscribed = true;
      if (this.display === 'after_dismiss') this.allowed = true;
      this.render();
      if (!this.hidden) this.button?.focus({ preventScroll: true });
    };
    this.onModalSubscribed = (event) => {
      if (!this.isMine(event)) return;
      this.subscribed = true;
      if (!this.designMode) storageSet(STORAGE_SUBSCRIBED, '1');
      this.render();
    };

    this.button.addEventListener('click', this.onOpenClick);
    this.closeButton?.addEventListener('click', this.onCloseClick);
    document.addEventListener('subscribe-modal:opened', this.onModalOpened);
    document.addEventListener('subscribe-modal:closed', this.onModalClosed);
    document.addEventListener('subscribe-modal:subscribed', this.onModalSubscribed);

    if (this.designMode) {
      this.allowed = true;
      this.render();
      return;
    }

    if (storageGet(STORAGE_SUBSCRIBED)) {
      this.subscribed = true;
      return;
    }

    const closedAt = Number(storageGet(STORAGE_CLOSED)) || 0;
    const closeDays = Math.max(1, Number(this.dataset.closeDays) || 60);
    if (closedAt && Date.now() - closedAt < closeDays * DAY) {
      this.closedByVisitor = true;
      return;
    }

    switch (this.display) {
      case 'after_delay': {
        const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
        this.timer = window.setTimeout(() => {
          this.allowed = true;
          this.render();
        }, delay);
        break;
      }
      case 'after_dismiss':
        if (storageGet(STORAGE_DISMISSED)) {
          this.allowed = true;
          this.render();
        }
        break;
      default:
        this.allowed = true;
        this.render();
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.button?.removeEventListener('click', this.onOpenClick);
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    document.removeEventListener('subscribe-modal:opened', this.onModalOpened);
    document.removeEventListener('subscribe-modal:closed', this.onModalClosed);
    document.removeEventListener('subscribe-modal:subscribed', this.onModalSubscribed);
  }

  /**
   * Events carry the popup section's id; with two popups on a page, react only to this tab's own.
   * @param {Event} event
   */
  isMine(event) {
    const id = event instanceof CustomEvent && event.detail ? event.detail.sectionId : '';
    return !id || !this.sectionId || id === this.sectionId;
  }

  render() {
    const visible = this.allowed && !this.modalOpen && !this.closedByVisitor && !this.subscribed;
    this.toggleAttribute('hidden', !visible);
  }

  openModal() {
    const modal =
      document.querySelector(`subscribe-modal[data-section-id="${this.sectionId}"]`) ||
      document.querySelector('subscribe-modal');
    if (modal && typeof (/** @type {any} */ (modal)).open === 'function') {
      /** @type {any} */ (modal).open();
      return;
    }
    document.dispatchEvent(new CustomEvent('subscribe-modal:open', { detail: { sectionId: this.sectionId } }));
  }

  closeTab() {
    this.closedByVisitor = true;
    if (!this.designMode) storageSet(STORAGE_CLOSED, String(Date.now()));
    this.render();
  }
}

if (!customElements.get('subscribe-teaser')) {
  customElements.define('subscribe-teaser', SubscribeTeaser);
}

/** @param {string} key */
function storageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 */
function storageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the tab simply comes back next visit.
  }
}
